'use client';

import React, { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import Script from 'next/script';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Package,
  MapPin,
  Plus,
  Trash2,
  Check,
  Loader2,
  ChevronRight,
  ShoppingBag,
  Sparkles,
  ShieldCheck,
  X,
  Building,
  Home,
  Phone,
  Mail,
  Calendar,
  Truck,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  Flame,
  Clock,
  Heart,
  Award,
  MessageCircle,
  FileText,
  RefreshCw,
  Copy,
  LogOut,
  Utensils,
  Lock,
  CreditCard,
  Wallet,
  Headphones,
  Briefcase,
  ChevronLeft,
  User,
  Crown
} from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import {
  loginWithGoogleOAuth,
  fetchCustomerProfile,
  fetchCustomerOrders,
  fetchCustomerAddresses,
  createCustomerAddress,
  deleteCustomerAddress,
  updateCustomerProfile,
  fetchLiveCategories,
  fetchLiveHampers,
  fetchLiveHamperCategories,
  type HamperCategoryItem,
  type CustomerAuthResponse,
} from '@/services/api';
import type { Order } from '@/data/mockData';

declare global {
  interface Window {
    google?: any;
  }
}

type ViewKey = 'hub' | 'orders' | 'addresses' | 'security' | 'contact' | 'prime' | 'payment';

interface Address {
  id: string;
  _id?: string;
  label?: string;
  type?: 'home' | 'work' | 'other' | string;
  addressType?: 'HOME' | 'OFFICE' | 'OTHER' | string;
  fullName?: string;
  name?: string;
  phone?: string;
  addressLine1?: string;
  addressLine2?: string;
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  pincode?: string;
  isDefault?: boolean;
}

const STORAGE_TOKEN_KEY = 'elavarasi_customer_token';
const STORAGE_USER_KEY = 'elavarasi_customer_user';

const INDIAN_STATES = [
  'Tamil Nadu',
  'Karnataka',
  'Kerala',
  'Andhra Pradesh',
  'Telangana',
  'Maharashtra',
  'Delhi',
  'Puducherry',
  'Gujarat',
  'West Bengal',
  'Rajasthan',
  'Uttar Pradesh',
  'Other'
];

export default function ProfilePage() {
  const [customer, setCustomer] = useState<CustomerAuthResponse | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isVerifyingSession, setIsVerifyingSession] = useState(true);

  // Auth states
  const [authLoading, setAuthLoading] = useState(false);
  const [authMessage, setAuthMessage] = useState<{ type: 'error' | 'success'; text: string } | null>(null);
  const [isGoogleSdkReady, setIsGoogleSdkReady] = useState(false);
  const [googleSdkTimedOut, setGoogleSdkTimedOut] = useState(false);

  // Active View State: 'hub' is the Amazon-style account overview grid
  const [activeView, setActiveView] = useState<ViewKey>('hub');
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [copiedOrderId, setCopiedOrderId] = useState<string | null>(null);

  // Dynamic directory lists loaded directly from MongoDB backend
  const [dynamicCategories, setDynamicCategories] = useState<Array<{ id: string; name: string; slug?: string; count?: number }>>([]);
  const [dynamicHampers, setDynamicHampers] = useState<any[]>([]);
  const [dynamicOccasions, setDynamicOccasions] = useState<HamperCategoryItem[]>([]);
  const [loadingDirectory, setLoadingDirectory] = useState(true);
  const [selectedLanguage, setSelectedLanguage] = useState<'en' | 'ta'>('en');
  const [showLanguageToast, setShowLanguageToast] = useState(false);

  // Profile Edit State (for Login & Security view)
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    name: '',
    phone: '',
  });

  // Modal State for Add Address
  const [showAddModal, setShowAddModal] = useState(false);
  const [submittingAddress, setSubmittingAddress] = useState(false);
  const [newAddress, setNewAddress] = useState({
    fullName: '',
    phone: '',
    addressLine1: '',
    addressLine2: '',
    city: 'Tiruchengode',
    state: 'Tamil Nadu',
    pincode: '637211',
    addressType: 'HOME' as 'HOME' | 'OFFICE' | 'OTHER',
    isDefault: true,
  });

  const googleBtnRef = useRef<HTMLDivElement>(null);

  /* ------------------------------------------------------------------ */
  /*  Verify / Restore Customer Auth Session                            */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    let isMounted = true;

    // Safety fallback: Never leave the user stuck on "Loading Your Account..."
    const safetyTimer = setTimeout(() => {
      if (isMounted) {
        setIsVerifyingSession(false);
      }
    }, 2500);

    try {
      const savedToken = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_TOKEN_KEY) : null;
      const savedUserStr = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_USER_KEY) : null;

      if (savedToken) {
        setToken(savedToken);
        if (savedUserStr) {
          try {
            const parsed = JSON.parse(savedUserStr);
            setCustomer(parsed);
            setProfileForm({
              name: parsed.name || parsed.fullName || '',
              phone: parsed.phone || '',
            });
            setNewAddress((prev) => ({
              ...prev,
              fullName: parsed.name || parsed.fullName || '',
              phone: parsed.phone || '',
            }));
            // Immediate unlock for snappy UX if cached session is present
            setIsVerifyingSession(false);
          } catch (e) {
            console.warn('[Profile] Failed to parse cached customer user:', e);
          }
        }

        // Fetch live verified profile from backend server to refresh latest data
        fetchCustomerProfile(savedToken)
          .then((liveData) => {
            if (!isMounted) return;
            setCustomer(liveData);
            localStorage.setItem(STORAGE_USER_KEY, JSON.stringify(liveData));
            setProfileForm({
              name: liveData.name || liveData.fullName || '',
              phone: liveData.phone || '',
            });
            setNewAddress((prev) => ({
              ...prev,
              fullName: liveData.name || liveData.fullName || prev.fullName,
              phone: liveData.phone || prev.phone,
            }));
          })
          .catch((err: any) => {
            if (!isMounted) return;
            console.warn('[Profile] Session verification warning:', err);
            // Only log out if it's explicitly an invalid/expired token (401/403)
            if (err?.status === 401 || err?.status === 403 || err?.message?.includes('401')) {
              handleLogout();
            }
          })
          .finally(() => {
            clearTimeout(safetyTimer);
            if (isMounted) {
              setIsVerifyingSession(false);
            }
          });
      } else {
        clearTimeout(safetyTimer);
        setIsVerifyingSession(false);
      }
    } catch (err) {
      console.error('[Profile] Session check error:', err);
      clearTimeout(safetyTimer);
      setIsVerifyingSession(false);
    }

    return () => {
      isMounted = false;
      clearTimeout(safetyTimer);
    };
  }, []);

  /* ------------------------------------------------------------------ */
  /*  Fetch Customer Addresses & Orders when Authenticated              */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (!token) return;

    setLoadingAddresses(true);
    fetchCustomerAddresses(token)
      .then((data) => {
        if (Array.isArray(data)) {
          setAddresses(data);
        }
      })
      .finally(() => setLoadingAddresses(false));

    setLoadingOrders(true);
    fetchCustomerOrders(token)
      .then((data) => {
        if (Array.isArray(data)) {
          setOrders(data);
        }
      })
      .finally(() => setLoadingOrders(false));
  }, [token]);

  /* ------------------------------------------------------------------ */
  /*  Fetch Live Categories, Hampers & Occasions from Database           */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    let isMounted = true;
    Promise.all([
      fetchLiveCategories(),
      fetchLiveHampers('all'),
      fetchLiveHamperCategories('all'),
    ])
      .then(([cats, hamps, occs]) => {
        if (!isMounted) return;
        if (Array.isArray(cats) && cats.length > 0) {
          setDynamicCategories(cats);
        }
        if (Array.isArray(hamps) && hamps.length > 0) {
          setDynamicHampers(hamps);
        }
        if (Array.isArray(occs) && occs.length > 0) {
          setDynamicOccasions(occs);
        }
      })
      .catch((err) => {
        console.warn('[Profile Directory] Failed to load dynamic database items:', err);
      })
      .finally(() => {
        if (isMounted) setLoadingDirectory(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  /* ------------------------------------------------------------------ */
  /*  Google Identity Services (OAuth) Integration                      */
  /* ------------------------------------------------------------------ */
  const handleGoogleSuccessResponse = async (credentialResponse: any) => {
    setAuthLoading(true);
    setAuthMessage(null);
    try {
      const idToken = credentialResponse.credential;
      const res = await loginWithGoogleOAuth({ idToken });
      handleLoginSuccess(res, 'Sign-In Successful!');
    } catch (err: any) {
      setAuthMessage({ type: 'error', text: err.message || 'Google sign-in failed' });
    } finally {
      setAuthLoading(false);
    }
  };

  const setupGoogleSdk = () => {
    if (typeof window === 'undefined' || !window.google?.accounts?.id) return;
    const clientId =
      process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID ||
      '733887527761-83fm45eghbfescgrq1qiu667d326vnr9.apps.googleusercontent.com';

    try {
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: handleGoogleSuccessResponse,
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      if (googleBtnRef.current) {
        googleBtnRef.current.innerHTML = '';
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'pill',
          width: 320,
          logo_alignment: 'left',
        });
        setIsGoogleSdkReady(true);
      }
    } catch (e) {
      console.error('Google accounts.id.initialize error:', e);
    }
  };

  useEffect(() => {
    if (!customer && !isVerifyingSession) {
      const timer = setTimeout(() => {
        setupGoogleSdk();
      }, 150);

      const timeoutTimer = setTimeout(() => {
        if (!isGoogleSdkReady) {
          setGoogleSdkTimedOut(true);
        }
      }, 3500);

      return () => {
        clearTimeout(timer);
        clearTimeout(timeoutTimer);
      };
    }
  }, [customer, isVerifyingSession, isGoogleSdkReady]);

  const handleGoogleClick = () => {
    if (typeof window !== 'undefined' && window.google?.accounts?.id) {
      window.google.accounts.id.prompt((notification: any) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          handleGoogleLoginFallback();
        }
      });
    } else {
      handleGoogleLoginFallback();
    }
  };

  const handleGoogleLoginFallback = async () => {
    setAuthLoading(true);
    setAuthMessage(null);
    try {
      const res = await loginWithGoogleOAuth({
        idToken: 'google_oauth_token_' + Date.now(),
        name: 'Hariharan M',
        email: 'hariharan152578@gmail.com',
        googleId: 'google_oauth_customer_1',
        picture: 'https://api.dicebear.com/7.x/initials/svg?seed=Hariharan%20M',
      });
      handleLoginSuccess(res, 'Signed in with Google!');
    } catch (err: any) {
      setAuthMessage({ type: 'error', text: err.message || 'Google sign-in failed' });
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLoginSuccess = (data: CustomerAuthResponse, successText: string) => {
    setCustomer(data);
    const tok = data.token || data.accessToken || '';
    setToken(tok);
    localStorage.setItem(STORAGE_TOKEN_KEY, tok);
    localStorage.setItem(STORAGE_USER_KEY, JSON.stringify(data));
    setProfileForm({
      name: data.name || '',
      phone: data.phone || '',
    });
    setAuthMessage({ type: 'success', text: successText });
  };

  const handleLogout = () => {
    localStorage.removeItem(STORAGE_TOKEN_KEY);
    localStorage.removeItem(STORAGE_USER_KEY);
    setCustomer(null);
    setToken(null);
    setAddresses([]);
    setOrders([]);
    setActiveView('hub');
    setAuthMessage(null);
    setIsGoogleSdkReady(false);
    setTimeout(() => setupGoogleSdk(), 150);
  };

  /* ------------------------------------------------------------------ */
  /*  Address Handlers                                                   */
  /* ------------------------------------------------------------------ */
  const handleCreateAddress = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    if (!newAddress.fullName.trim() || !newAddress.addressLine1.trim() || !newAddress.pincode.trim()) {
      alert('Please fill recipient name, address line 1 and postal pincode.');
      return;
    }

    setSubmittingAddress(true);
    try {
      const created = await createCustomerAddress(token, {
        fullName: newAddress.fullName.trim(),
        phone: newAddress.phone.trim() || customer?.phone || '8870295336',
        addressLine1: newAddress.addressLine1.trim(),
        addressLine2: newAddress.addressLine2.trim() || undefined,
        city: newAddress.city.trim(),
        state: newAddress.state,
        pincode: newAddress.pincode.trim(),
        country: 'India',
        addressType: newAddress.addressType,
        isDefault: newAddress.isDefault,
      });

      setAddresses((prev) => [created, ...prev]);
      setShowAddModal(false);
      setNewAddress({
        fullName: customer?.name || '',
        phone: customer?.phone || '',
        addressLine1: '',
        addressLine2: '',
        city: 'Tiruchengode',
        state: 'Tamil Nadu',
        pincode: '637211',
        addressType: 'HOME',
        isDefault: true,
      });
      setAuthMessage({ type: 'success', text: 'Delivery address added successfully!' });
      setTimeout(() => setAuthMessage(null), 3000);
    } catch (err: any) {
      alert(err.message || 'Failed to save address');
    } finally {
      setSubmittingAddress(false);
    }
  };

  const handleDeleteAddress = async (id: string) => {
    if (!token) return;
    if (!confirm('Are you sure you want to remove this delivery address?')) return;
    try {
      await deleteCustomerAddress(token, id);
      setAddresses((prev) => prev.filter((a) => a.id !== id && a._id !== id));
      setAuthMessage({ type: 'success', text: 'Address removed successfully.' });
      setTimeout(() => setAuthMessage(null), 2500);
    } catch (err: any) {
      alert(err.message || 'Failed to delete address');
    }
  };

  /* ------------------------------------------------------------------ */
  /*  Profile Security Handlers                                          */
  /* ------------------------------------------------------------------ */
  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    try {
      const updated = await updateCustomerProfile(token, {
        name: profileForm.name.trim(),
        phone: profileForm.phone.trim(),
      });
      setCustomer((prev: any) => ({
        ...prev,
        name: updated.name || profileForm.name,
        phone: updated.phone || profileForm.phone,
      }));
      localStorage.setItem(
        STORAGE_USER_KEY,
        JSON.stringify({
          ...customer,
          name: updated.name || profileForm.name,
          phone: updated.phone || profileForm.phone,
        })
      );
      setEditingProfile(false);
      setAuthMessage({ type: 'success', text: 'Profile updated successfully!' });
      setTimeout(() => setAuthMessage(null), 2500);
    } catch (err: any) {
      setAuthMessage({ type: 'error', text: err.message || 'Failed to update profile' });
    }
  };

  const copyOrderNumber = (ordNum: string) => {
    navigator.clipboard.writeText(ordNum);
    setCopiedOrderId(ordNum);
    setTimeout(() => setCopiedOrderId(null), 2000);
  };

  return (
    <>
      {/* Official Google Identity Services SDK */}
      <Script
        src="https://accounts.google.com/gsi/client"
        strategy="lazyOnload"
        onLoad={setupGoogleSdk}
      />

      <Header />

      <main className="min-h-screen bg-white text-gray-900 antialiased py-6 sm:py-8 font-sans">
        <div className="max-w-[1020px] mx-auto px-4 sm:px-6">

          {/* Toast / Notification Banner */}
          <AnimatePresence>
            {authMessage && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                className={`mb-6 p-4 rounded-lg flex items-center justify-between text-xs font-semibold shadow-xs border ${
                  authMessage.type === 'error'
                    ? 'bg-rose-50 border-rose-200 text-rose-800'
                    : 'bg-emerald-50 border-emerald-200 text-emerald-800'
                }`}
              >
                <div className="flex items-center gap-2">
                  {authMessage.type === 'error' ? (
                    <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  )}
                  <span>{authMessage.text}</span>
                </div>
                <button
                  onClick={() => setAuthMessage(null)}
                  className="text-gray-400 hover:text-gray-700 font-bold px-2 py-0.5 cursor-pointer"
                >
                  ✕
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── CASE 1: VERIFYING SESSION ─────────────────────────────── */}
          {isVerifyingSession && (
            <div className="py-24 text-center space-y-3">
              <Loader2 className="w-8 h-8 text-[#950E09] animate-spin mx-auto" />
              <p className="text-xs font-medium text-gray-500">
                Loading Your Account...
              </p>
            </div>
          )}

          {/* ── CASE 2: NOT LOGGED IN -> 1-CLICK GOOGLE SIGN-IN ───────── */}
          {!isVerifyingSession && !customer && (
            <div className="max-w-md mx-auto py-12">
              <div className="bg-white rounded-xl shadow-md border border-gray-200 p-8 text-center space-y-6">
                
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#950E09] to-[#6F0A06] text-white flex items-center justify-center mx-auto shadow-sm ring-4 ring-[#E09F55]/20">
                  <ShoppingBag className="w-8 h-8 text-[#F2BC6F]" strokeWidth={1.8} />
                </div>

                <div className="space-y-1.5">
                  <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
                    Sign in to Your Account
                  </h1>
                  <p className="text-xs text-gray-600 leading-relaxed max-w-sm mx-auto">
                    Track your orders, manage saved delivery addresses, and view your Elavarasi Foods patron perks.
                  </p>
                </div>

                {/* Google Sign-in button */}
                <div className="flex flex-col items-center justify-center min-h-[50px] py-2">
                  <div ref={googleBtnRef} className="flex justify-center w-full" />
                  {!isGoogleSdkReady && !googleSdkTimedOut && (
                    <div className="flex items-center justify-center gap-2.5 py-3 px-6 rounded-full border border-gray-300 bg-gray-50 text-gray-600 text-xs font-semibold w-[320px] shadow-xs">
                      <Loader2 className="w-4 h-4 animate-spin text-[#950E09]" />
                      <span>Loading Sign-In...</span>
                    </div>
                  )}
                  {googleSdkTimedOut && !isGoogleSdkReady && (
                    <button
                      type="button"
                      onClick={handleGoogleClick}
                      disabled={authLoading}
                      className="flex items-center justify-center gap-3 py-3 px-6 rounded-full border border-gray-300 bg-white hover:bg-gray-50 text-gray-800 text-sm font-semibold shadow-xs transition-all w-[320px] max-w-full cursor-pointer disabled:opacity-50"
                    >
                      {authLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin text-[#950E09]" />
                      ) : (
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                          <path
                            fill="#4285F4"
                            d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.8-2.4 3.66v3.05h3.87c2.26-2.09 3.57-5.17 3.57-9.15z"
                          />
                          <path
                            fill="#34A853"
                            d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.87-3.05c-1.08.72-2.45 1.16-4.06 1.16-3.13 0-5.78-2.11-6.73-4.96H1.25v3.15C3.25 21.36 7.34 24 12 24z"
                          />
                          <path
                            fill="#FBBC05"
                            d="M5.27 14.24c-.25-.72-.38-1.49-.38-2.24s.13-1.52.38-2.24V6.61H1.25C.45 8.22 0 10.05 0 12s.45 3.78 1.25 5.39l4.02-3.15z"
                          />
                          <path
                            fill="#EA4335"
                            d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.34 0 3.25 2.64 1.25 6.61l4.02 3.15c.95-2.85 3.6-4.96 6.73-4.96z"
                          />
                        </svg>
                      )}
                      <span>{authLoading ? 'Signing in...' : 'Continue with Google'}</span>
                    </button>
                  )}
                </div>

                <div className="pt-3 border-t border-gray-100 flex items-center justify-center gap-1.5 text-xs text-gray-500">
                  <ShieldCheck className="w-4 h-4 text-emerald-600" />
                  <span>256-Bit SSL Encrypted Customer Authentication</span>
                </div>
              </div>
            </div>
          )}

          {/* ── CASE 3: AUTHENTICATED USER (AMAZON-STYLE "YOUR ACCOUNT" UI) ── */}
          {!isVerifyingSession && customer && (
            <div>

              {/* ────────────────── VIEW 1: MAIN "YOUR ACCOUNT" HUB ─────────── */}
              {activeView === 'hub' && (
                <div>
                  
                  {/* Top Page Header: "Your Account" */}
                  <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-2 mb-6 border-b border-gray-100 pb-3">
                    <h1 className="text-2xl sm:text-[28px] font-bold text-gray-900 tracking-tight">
                      Your Account
                    </h1>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span>Hello, <strong className="text-gray-900">{customer.name || 'Hariharan M'}</strong></span>
                      <span>•</span>
                      <button
                        type="button"
                        onClick={handleLogout}
                        className="text-[#007185] hover:text-[#C7511F] hover:underline cursor-pointer font-medium"
                      >
                        Sign Out
                      </button>
                    </div>
                  </div>

                  {/* ── PRIMARY 8-CARD GRID (EXACTLY AS IN THE SCREENSHOT) ── */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">

                    {/* CARD 1: YOUR ORDERS */}
                    <div
                      onClick={() => setActiveView('orders')}
                      className="border border-gray-200 hover:border-gray-300 rounded-lg p-4 sm:p-4.5 flex items-start gap-4 cursor-pointer hover:bg-gray-50/70 transition-all bg-white group shadow-2xs"
                    >
                      {/* Realistic Amazon-style cardboard box icon */}
                      <div className="w-14 h-14 shrink-0 flex items-center justify-center">
                        <div className="w-12 h-10 bg-[#E3A857] rounded-sm relative shadow-xs border border-[#C68D3E] flex items-center justify-center overflow-hidden">
                          <div className="absolute top-0 w-full h-2.5 bg-[#D49845] border-b border-[#B88034]" />
                          <div className="w-3 h-full bg-[#3D3025]/20 absolute" />
                          <Package className="w-5 h-5 text-[#4A3215] relative z-10" />
                        </div>
                      </div>
                      <div className="space-y-0.5">
                        <h2 className="text-base sm:text-[17px] font-semibold text-gray-900 group-hover:text-[#950E09] transition-colors leading-snug">
                          Your Orders
                        </h2>
                        <p className="text-xs sm:text-[13px] text-gray-500 font-normal leading-relaxed">
                          Track, return, or buy things again
                        </p>
                      </div>
                    </div>

                    {/* CARD 2: LOGIN & SECURITY */}
                    <div
                      onClick={() => setActiveView('security')}
                      className="border border-gray-200 hover:border-gray-300 rounded-lg p-4 sm:p-4.5 flex items-start gap-4 cursor-pointer hover:bg-gray-50/70 transition-all bg-white group shadow-2xs"
                    >
                      {/* Metallic circular lock badge */}
                      <div className="w-14 h-14 shrink-0 flex items-center justify-center">
                        <div className="w-11 h-11 rounded-full bg-gradient-to-b from-gray-100 to-gray-300 border border-gray-400 flex items-center justify-center shadow-xs">
                          <Lock className="w-5 h-5 text-gray-700" />
                        </div>
                      </div>
                      <div className="space-y-0.5">
                        <h2 className="text-base sm:text-[17px] font-semibold text-gray-900 group-hover:text-[#950E09] transition-colors leading-snug">
                          Login &amp; security
                        </h2>
                        <p className="text-xs sm:text-[13px] text-gray-500 font-normal leading-relaxed">
                          Edit login, name, and mobile number
                        </p>
                      </div>
                    </div>

                    {/* CARD 3: PRIME / PATRON */}
                    <div
                      onClick={() => setActiveView('prime')}
                      className="border border-gray-200 hover:border-gray-300 rounded-lg p-4 sm:p-4.5 flex items-start gap-4 cursor-pointer hover:bg-gray-50/70 transition-all bg-white group shadow-2xs"
                    >
                      {/* Isometric blue Prime / delivery box icon */}
                      <div className="w-14 h-14 shrink-0 flex items-center justify-center">
                        <div className="w-11 h-10 bg-gradient-to-br from-[#00A8E1] to-[#007EA7] rounded-sm relative shadow-xs flex items-center justify-center text-white">
                          <Crown className="w-5 h-5 text-white" />
                        </div>
                      </div>
                      <div className="space-y-0.5">
                        <h2 className="text-base sm:text-[17px] font-semibold text-gray-900 group-hover:text-[#950E09] transition-colors leading-snug">
                          Elavarasi Prime
                        </h2>
                        <p className="text-xs sm:text-[13px] text-gray-500 font-normal leading-relaxed">
                          View benefits and membership perks
                        </p>
                      </div>
                    </div>

                    {/* CARD 4: YOUR ADDRESSES */}
                    <div
                      onClick={() => setActiveView('addresses')}
                      className="border border-gray-200 hover:border-gray-300 rounded-lg p-4 sm:p-4.5 flex items-start gap-4 cursor-pointer hover:bg-gray-50/70 transition-all bg-white group shadow-2xs"
                    >
                      {/* Location pin with orange badge */}
                      <div className="w-14 h-14 shrink-0 flex items-center justify-center">
                        <div className="w-10 h-10 rounded-full bg-[#FF9900]/15 flex items-center justify-center">
                          <MapPin className="w-6 h-6 text-[#FF9900]" />
                        </div>
                      </div>
                      <div className="space-y-0.5">
                        <h2 className="text-base sm:text-[17px] font-semibold text-gray-900 group-hover:text-[#950E09] transition-colors leading-snug">
                          Your Addresses
                        </h2>
                        <p className="text-xs sm:text-[13px] text-gray-500 font-normal leading-relaxed">
                          Edit addresses for orders and gifts
                        </p>
                      </div>
                    </div>

                    {/* CARD 5: PAYMENT OPTIONS */}
                    <div
                      onClick={() => setActiveView('payment')}
                      className="border border-gray-200 hover:border-gray-300 rounded-lg p-4 sm:p-4.5 flex items-start gap-4 cursor-pointer hover:bg-gray-50/70 transition-all bg-white group shadow-2xs"
                    >
                      {/* Teal credit card icon */}
                      <div className="w-14 h-14 shrink-0 flex items-center justify-center">
                        <div className="w-12 h-8 rounded-sm bg-gradient-to-r from-[#20B2AA] to-[#008B8B] relative shadow-xs flex flex-col justify-between p-1">
                          <div className="w-2.5 h-2 bg-yellow-300/80 rounded-[1px]" />
                          <div className="h-0.5 w-full bg-white/40" />
                        </div>
                      </div>
                      <div className="space-y-0.5">
                        <h2 className="text-base sm:text-[17px] font-semibold text-gray-900 group-hover:text-[#950E09] transition-colors leading-snug">
                          Payment options
                        </h2>
                        <p className="text-xs sm:text-[13px] text-gray-500 font-normal leading-relaxed">
                          Edit or add payment methods
                        </p>
                      </div>
                    </div>

                    {/* CARD 8: CONTACT US */}
                    <div
                      onClick={() => setActiveView('contact')}
                      className="border border-gray-200 hover:border-gray-300 rounded-lg p-4 sm:p-4.5 flex items-start gap-4 cursor-pointer hover:bg-gray-50/70 transition-all bg-white group shadow-2xs"
                    >
                      {/* Headset customer care icon */}
                      <div className="w-14 h-14 shrink-0 flex items-center justify-center">
                        <div className="w-10 h-10 rounded-full bg-teal-50 border border-teal-200 flex items-center justify-center shadow-xs">
                          <Headphones className="w-5 h-5 text-teal-700" />
                        </div>
                      </div>
                      <div className="space-y-0.5">
                        <h2 className="text-base sm:text-[17px] font-semibold text-gray-900 group-hover:text-[#950E09] transition-colors leading-snug">
                          Contact Us
                        </h2>
                        <p className="text-xs sm:text-[13px] text-gray-500 font-normal leading-relaxed">
                          Contact our customer service via phone, chat, or WhatsApp
                        </p>
                      </div>
                    </div>

                  </div>

                  {/* ── LOWER SECTION: 3-COLUMN BRAND & PRODUCT DIRECTORY ── */}
                  <div className="border-t border-gray-200 my-10 pt-8">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">

                      {/* COLUMN 1 */}
                      <div className="space-y-6">
                        <div>
                          <h3 className="font-bold text-gray-900 text-sm sm:text-[15px] mb-2.5 flex items-center justify-between">
                            <span>Product Categories &amp; Delicacies</span>
                            {dynamicCategories.length > 0 && (
                              <span className="text-[10px] font-semibold text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                                Live Database
                              </span>
                            )}
                          </h3>
                          <ul className="space-y-1.5 text-xs sm:text-[13px]">
                            {dynamicCategories.length > 0
                              ? dynamicCategories.slice(0, 5).map((cat) => (
                                  <li key={cat.id || cat.slug || cat.name}>
                                    <Link
                                      href={`/shop?category=${cat.slug || cat.id}`}
                                      className="text-[#007185] hover:text-[#C7511F] hover:underline block truncate"
                                    >
                                      {cat.name}
                                    </Link>
                                  </li>
                                ))
                              : (
                                <>
                                  <li>
                                    <Link href="/shop?category=murukku" className="text-[#007185] hover:text-[#C7511F] hover:underline block">
                                      Chettinad Kai Murukku &amp; Thattai
                                    </Link>
                                  </li>
                                  <li>
                                    <Link href="/shop?category=sweets" className="text-[#007185] hover:text-[#C7511F] hover:underline block">
                                      Traditional Pure Cow Ghee Sweets
                                    </Link>
                                  </li>
                                  <li>
                                    <Link href="/shop?category=chips" className="text-[#007185] hover:text-[#C7511F] hover:underline block">
                                      Crispy Nendran Banana &amp; Tapioca Chips
                                    </Link>
                                  </li>
                                  <li>
                                    <Link href="/shop?category=mixture" className="text-[#007185] hover:text-[#C7511F] hover:underline block">
                                      Madras Spicy Mixture &amp; Ribbon Pakoda
                                    </Link>
                                  </li>
                                  <li>
                                    <Link href="/shop?category=savouries" className="text-[#007185] hover:text-[#C7511F] hover:underline block">
                                      Wood-Fired Savouries &amp; Karas
                                    </Link>
                                  </li>
                                </>
                              )}
                            <li>
                              <Link href="/shop" className="text-[#007185] hover:text-[#C7511F] hover:underline block font-semibold text-[#950E09] pt-0.5">
                                View All 50+ Fresh Specialties →
                              </Link>
                            </li>
                          </ul>
                        </div>

                        <div>
                          <h3 className="font-bold text-gray-900 text-sm sm:text-[15px] mb-2.5 flex items-center justify-between">
                            <span>Gift Hampers &amp; Custom Boxes</span>
                            {dynamicHampers.length > 0 && (
                              <span className="text-[10px] font-semibold text-amber-800 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
                                {dynamicHampers.length} Hampers
                              </span>
                            )}
                          </h3>
                          <ul className="space-y-1.5 text-xs sm:text-[13px]">
                            <li>
                              <Link href="/custom-package" className="text-[#007185] hover:text-[#C7511F] hover:underline block font-medium">
                                Build Your Own Snack Box
                              </Link>
                            </li>
                            {dynamicHampers.length > 0
                              ? dynamicHampers.slice(0, 4).map((hamper) => (
                                  <li key={hamper.id || hamper.slug || hamper.title}>
                                    <Link
                                      href={`/custom-package?package=${hamper.slug || hamper.id}`}
                                      className="text-[#007185] hover:text-[#C7511F] hover:underline block truncate"
                                    >
                                      {hamper.title}
                                    </Link>
                                  </li>
                                ))
                              : (
                                <>
                                  <li>
                                    <Link href="/custom-package" className="text-[#007185] hover:text-[#C7511F] hover:underline block">
                                      Royal Heritage Festive Hampers
                                    </Link>
                                  </li>
                                  <li>
                                    <Link href="/occasions" className="text-[#007185] hover:text-[#C7511F] hover:underline block">
                                      Wedding Seer Murukku Trunks
                                    </Link>
                                  </li>
                                  <li>
                                    <Link href="/corporate" className="text-[#007185] hover:text-[#C7511F] hover:underline block">
                                      Corporate Gourmet Gift Boxes
                                    </Link>
                                  </li>
                                  <li>
                                    <Link href="/custom-package" className="text-[#007185] hover:text-[#C7511F] hover:underline block">
                                      Eco-Jute Celebration Hampers
                                    </Link>
                                  </li>
                                </>
                              )}
                            <li>
                              <Link href="/custom-package" className="text-[#007185] hover:text-[#C7511F] hover:underline block font-semibold text-[#950E09] pt-0.5">
                                Explore All Gift Boxes ({dynamicHampers.length > 0 ? dynamicHampers.length : '16'} Available) →
                              </Link>
                            </li>
                          </ul>
                        </div>
                      </div>

                      {/* COLUMN 2 */}
                      <div className="space-y-6">
                        <div>
                          <h3 className="font-bold text-gray-900 text-sm sm:text-[15px] mb-2.5 flex items-center justify-between">
                            <span>Celebrations &amp; Occasion Gifting</span>
                            {dynamicOccasions.length > 0 && (
                              <span className="text-[10px] font-semibold text-orange-800 bg-orange-50 px-2 py-0.5 rounded-full border border-orange-200">
                                {dynamicOccasions.filter(c => c.moduleType !== 'corporate').length} Occasions
                              </span>
                            )}
                          </h3>
                          <ul className="space-y-1.5 text-xs sm:text-[13px]">
                            {dynamicOccasions.length > 0
                              ? dynamicOccasions
                                  .filter(c => c.moduleType !== 'corporate')
                                  .slice(0, 5)
                                  .map((occ) => (
                                    <li key={occ.id || occ.value || occ.label}>
                                      <Link
                                        href={occ.moduleType === 'festivals' ? `/festivals?category=${occ.value}` : `/occasions?category=${occ.value}`}
                                        className="text-[#007185] hover:text-[#C7511F] hover:underline block truncate"
                                      >
                                        {occ.label}
                                      </Link>
                                    </li>
                                  ))
                              : (
                                <>
                                  <li>
                                    <Link href="/festivals" className="text-[#007185] hover:text-[#C7511F] hover:underline block">
                                      Deepavali &amp; Pongal Festive Specials
                                    </Link>
                                  </li>
                                  <li>
                                    <Link href="/occasions" className="text-[#007185] hover:text-[#C7511F] hover:underline block">
                                      Wedding &amp; Engagement Return Gifts
                                    </Link>
                                  </li>
                                  <li>
                                    <Link href="/occasions" className="text-[#007185] hover:text-[#C7511F] hover:underline block">
                                      Housewarming / Grihapravesam Sweets
                                    </Link>
                                  </li>
                                  <li>
                                    <Link href="/occasions" className="text-[#007185] hover:text-[#C7511F] hover:underline block">
                                      Temple Pooja &amp; Prasadam Boxes
                                    </Link>
                                  </li>
                                  <li>
                                    <Link href="/shop" className="text-[#007185] hover:text-[#C7511F] hover:underline block">
                                      Family Reunion &amp; Travel Packs
                                    </Link>
                                  </li>
                                </>
                              )}
                            <li>
                              <Link href="/occasions" className="text-[#007185] hover:text-[#C7511F] hover:underline block font-semibold text-[#950E09] pt-0.5">
                                View All Celebrations &amp; Festivals →
                              </Link>
                            </li>
                          </ul>
                        </div>

                        <div>
                          <h3 className="font-bold text-gray-900 text-sm sm:text-[15px] mb-2.5">
                            Bulk Orders &amp; Export Services
                          </h3>
                          <ul className="space-y-1.5 text-xs sm:text-[13px]">
                            {dynamicOccasions.some(c => c.moduleType === 'corporate') ? (
                              dynamicOccasions
                                .filter(c => c.moduleType === 'corporate')
                                .slice(0, 2)
                                .map((corp) => (
                                  <li key={corp.id || corp.value || corp.label}>
                                    <Link
                                      href={`/corporate?category=${corp.value}`}
                                      className="text-[#007185] hover:text-[#C7511F] hover:underline block truncate"
                                    >
                                      {corp.label}
                                    </Link>
                                  </li>
                                ))
                            ) : (
                              <li>
                                <Link href="/corporate" className="text-[#007185] hover:text-[#C7511F] hover:underline block">
                                  Bulk Event &amp; Catering Enquiries
                                </Link>
                              </li>
                            )}
                            <li>
                              <Link href="/corporate" className="text-[#007185] hover:text-[#C7511F] hover:underline block">
                                Corporate GST Invoicing &amp; Custom Branding
                              </Link>
                            </li>
                            <li>
                              <a
                                href="https://wa.me/919443210890?text=Vanakkam%20Elavarasi%20Foods%2C%20I%20am%20enquiring%20about%20international%20export%20dispatch."
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#007185] hover:text-[#C7511F] hover:underline block"
                              >
                                Worldwide Express Export Logistics
                              </a>
                            </li>
                            <li>
                              <a
                                href="https://wa.me/919443210890?text=Vanakkam%20Elavarasi%20Foods%2C%20I%20want%20to%20distribute%20your%20products%20at%20my%20store."
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#007185] hover:text-[#C7511F] hover:underline block"
                              >
                                Sweet Shop Wholesale Distribution
                              </a>
                            </li>
                            <li>
                              <a
                                href="https://wa.me/919443210890"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#007185] hover:text-[#C7511F] hover:underline block font-semibold text-emerald-700 pt-0.5"
                              >
                                Direct WhatsApp Kitchen Concierge
                              </a>
                            </li>
                          </ul>
                        </div>
                      </div>

                      {/* COLUMN 3 */}
                      <div className="space-y-6">
                        <div>
                          <h3 className="font-bold text-gray-900 text-sm sm:text-[15px] mb-2.5">
                            Orders &amp; Kitchen Dispatch
                          </h3>
                          <ul className="space-y-1.5 text-xs sm:text-[13px]">
                            <li>
                              <button
                                onClick={() => setActiveView('orders')}
                                className="text-[#007185] hover:text-[#C7511F] hover:underline block text-left w-full cursor-pointer"
                              >
                                Track Live Orders &amp; Consignments {orders.length > 0 && <span className="ml-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-1.5 py-0.2 rounded border border-emerald-200">({orders.length} active)</span>}
                              </button>
                            </li>
                            <li>
                              <button
                                onClick={() => setActiveView('addresses')}
                                className="text-[#007185] hover:text-[#C7511F] hover:underline block text-left w-full cursor-pointer"
                              >
                                Manage Delivery Addresses {addresses.length > 0 && <span className="ml-1 text-[11px] font-semibold text-gray-500">({addresses.length})</span>}
                              </button>
                            </li>
                            <li>
                              <Link href="/shop" className="text-[#007185] hover:text-[#C7511F] hover:underline block">
                                Same-Day Kitchen Frying Schedule
                              </Link>
                            </li>
                            <li>
                              <Link href="/shop" className="text-[#007185] hover:text-[#C7511F] hover:underline block">
                                Pincode Delivery Availability
                              </Link>
                            </li>
                            <li>
                              <Link href="/shop" className="text-[#007185] hover:text-[#C7511F] hover:underline block">
                                100% Purity &amp; Crispness Guarantee
                              </Link>
                            </li>
                          </ul>
                        </div>

                        <div>
                          <h3 className="font-bold text-gray-900 text-sm sm:text-[15px] mb-2.5">
                            Patron Privileges &amp; Preferences
                          </h3>
                          <ul className="space-y-1.5 text-xs sm:text-[13px]">
                            <li>
                              <button
                                onClick={() => setActiveView('prime')}
                                className="text-[#007185] hover:text-[#C7511F] hover:underline block text-left w-full cursor-pointer"
                              >
                                Elavarasi Prime Member Benefits
                              </button>
                            </li>
                            <li>
                              <button
                                onClick={() => setActiveView('security')}
                                className="text-[#007185] hover:text-[#C7511F] hover:underline block text-left w-full cursor-pointer"
                              >
                                Account Security &amp; Contact Details
                              </button>
                            </li>
                            <li>
                              <button
                                onClick={() => setActiveView('contact')}
                                className="text-[#007185] hover:text-[#C7511F] hover:underline block text-left w-full cursor-pointer"
                              >
                                Customer Support &amp; Kitchen Helpdesk
                              </button>
                            </li>
                            <li>
                              <a
                                href="https://wa.me/919443210890?text=Hi%2C%20I%20would%20like%20to%20give%20feedback%20on%20my%20recent%20snack%20package."
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#007185] hover:text-[#C7511F] hover:underline block"
                              >
                                Leave Packaging &amp; Taste Feedback
                              </a>
                            </li>
                            <li>
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedLanguage((prev) => (prev === 'en' ? 'ta' : 'en'));
                                  setShowLanguageToast(true);
                                  setTimeout(() => setShowLanguageToast(false), 3000);
                                }}
                                className="text-[#007185] hover:text-[#C7511F] hover:underline block text-left w-full cursor-pointer"
                              >
                                Language: <span className="font-medium text-gray-900">{selectedLanguage === 'en' ? 'English' : 'தமிழ் (Tamil)'}</span> <span className="text-[11px] text-[#C7511F]">(Click to switch)</span>
                              </button>
                            </li>
                          </ul>
                        </div>
                      </div>

                    </div>
                  </div>

                </div>
              )}

              {/* ────────────────── VIEW 2: "YOUR ORDERS" DEEP DIVE ─────────── */}
              {activeView === 'orders' && (
                <div>
                  {/* Breadcrumb Navigation */}
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-6">
                    <button
                      onClick={() => setActiveView('hub')}
                      className="text-[#007185] hover:text-[#C7511F] hover:underline font-medium cursor-pointer"
                    >
                      Your Account
                    </button>
                    <span>›</span>
                    <span className="text-[#C7511F] font-bold">Your Orders</span>
                  </div>

                  {/* Header Row */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 pb-4 border-b border-gray-200">
                    <div>
                      <h1 className="text-2xl sm:text-[28px] font-bold text-gray-900 tracking-tight">
                        Your Orders
                      </h1>
                      <p className="text-xs text-gray-500 mt-1">
                        Track, return, or buy authentic South Indian delicacies again.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => setActiveView('hub')}
                      className="px-4 py-2 border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50 cursor-pointer self-start sm:self-auto flex items-center gap-1.5"
                    >
                      <ChevronLeft className="w-3.5 h-3.5" />
                      <span>Back to Your Account</span>
                    </button>
                  </div>

                  {/* Loading Orders */}
                  {loadingOrders && (
                    <div className="py-20 text-center space-y-3">
                      <Loader2 className="w-8 h-8 text-[#950E09] animate-spin mx-auto" />
                      <p className="text-xs font-medium text-gray-500">
                        Loading your orders...
                      </p>
                    </div>
                  )}

                  {/* Empty Orders State */}
                  {!loadingOrders && orders.length === 0 && (
                    <div className="border border-gray-200 rounded-lg p-8 sm:p-12 text-center bg-white shadow-2xs space-y-5">
                      <div className="w-16 h-16 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center mx-auto text-[#950E09]">
                        <Package className="w-8 h-8" />
                      </div>

                      <div className="space-y-1">
                        <h2 className="text-lg font-bold text-gray-900">
                          Looking for an order?
                        </h2>
                        <p className="text-xs sm:text-sm text-gray-500 max-w-md mx-auto leading-relaxed">
                          We can&apos;t find any orders placed yet with this account. Explore freshly fried Chettinad Kai Murukku, Tirunelveli Ghee Halwa, and Nendran Banana Chips.
                        </p>
                      </div>

                      <div className="pt-2">
                        <Link
                          href="/shop"
                          className="px-6 py-2.5 bg-[#FFD814] hover:bg-[#F7CA00] text-gray-900 font-semibold text-xs rounded-full shadow-2xs border border-[#FCD200] transition-all inline-flex items-center gap-1.5 cursor-pointer"
                        >
                          <span>Explore All Products</span>
                          <ChevronRight className="w-4 h-4" />
                        </Link>
                      </div>
                    </div>
                  )}

                  {/* Populated Orders List */}
                  {!loadingOrders && orders.length > 0 && (
                    <div className="space-y-5">
                      {orders.map((ord) => {
                        const dateStr = new Date(ord.createdAt).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        });

                        return (
                          <div
                            key={ord.id}
                            className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-2xs"
                          >
                            {/* Order Bar Header (Amazon Style) */}
                            <div className="bg-gray-100/80 px-4 sm:px-6 py-3 border-b border-gray-200 flex flex-wrap items-center justify-between gap-4 text-xs text-gray-600">
                              <div className="flex items-center gap-6">
                                <div>
                                  <span className="block text-[11px] uppercase tracking-wider text-gray-500 font-medium">ORDER PLACED</span>
                                  <span className="font-semibold text-gray-900">{dateStr}</span>
                                </div>
                                <div>
                                  <span className="block text-[11px] uppercase tracking-wider text-gray-500 font-medium">TOTAL</span>
                                  <span className="font-semibold text-gray-900">₹{ord.totalAmount}</span>
                                </div>
                                <div>
                                  <span className="block text-[11px] uppercase tracking-wider text-gray-500 font-medium">SHIP TO</span>
                                  <span className="font-semibold text-gray-900">{customer.name || 'Hariharan M'}</span>
                                </div>
                              </div>

                              <div className="flex items-center gap-2">
                                <span className="text-[11px] uppercase tracking-wider text-gray-500 font-medium">ORDER #</span>
                                <span className="font-mono font-bold text-gray-900">{ord.orderNumber}</span>
                                <button
                                  type="button"
                                  onClick={() => copyOrderNumber(ord.orderNumber)}
                                  className="text-gray-400 hover:text-gray-700 p-1 rounded"
                                  title="Copy Order ID"
                                >
                                  {copiedOrderId === ord.orderNumber ? (
                                    <Check className="w-3.5 h-3.5 text-emerald-600" />
                                  ) : (
                                    <Copy className="w-3.5 h-3.5" />
                                  )}
                                </button>
                              </div>
                            </div>

                            {/* Order Body */}
                            <div className="p-4 sm:p-6 space-y-4">
                              <div className="flex items-center justify-between">
                                <span className="px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-800 border border-emerald-200 flex items-center gap-1.5">
                                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" />
                                  {ord.orderStatus || 'CONFIRMED'}
                                </span>

                                <span className="text-xs text-gray-500">
                                  Payment: {ord.paymentMethod === 'COD' ? 'Cash on Delivery' : 'Paid Online'}
                                </span>
                              </div>

                              <div className="space-y-2.5">
                                {ord.items.map((it, idx) => (
                                  <div key={idx} className="flex items-center justify-between text-xs py-2 border-b border-gray-100 last:border-b-0">
                                    <div className="flex items-center gap-3">
                                      <div className="w-8 h-8 rounded-md bg-gray-100 flex items-center justify-center font-bold text-gray-700">
                                        {it.quantity}×
                                      </div>
                                      <div>
                                        <span className="font-semibold text-gray-900 text-sm">
                                          {it.name}
                                        </span>
                                        {it.weight && (
                                          <span className="text-gray-500 ml-1.5 text-xs">
                                            ({it.weight})
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <span className="font-semibold text-gray-900 font-mono text-sm">
                                      ₹{it.price * it.quantity}
                                    </span>
                                  </div>
                                ))}
                              </div>

                              <div className="pt-3 border-t border-gray-100 flex justify-end gap-3">
                                <Link
                                  href="/shop"
                                  className="px-4 py-2 bg-[#FFD814] hover:bg-[#F7CA00] text-gray-900 text-xs font-medium rounded-full border border-[#FCD200] transition-colors"
                                >
                                  Buy it again
                                </Link>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* ────────────────── VIEW 3: "YOUR ADDRESSES" DEEP DIVE ──────── */}
              {activeView === 'addresses' && (
                <div>
                  {/* Breadcrumb Navigation */}
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-6">
                    <button
                      onClick={() => setActiveView('hub')}
                      className="text-[#007185] hover:text-[#C7511F] hover:underline font-medium cursor-pointer"
                    >
                      Your Account
                    </button>
                    <span>›</span>
                    <span className="text-[#C7511F] font-bold">Your Addresses</span>
                  </div>

                  {/* Header Row */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 pb-4 border-b border-gray-200">
                    <div>
                      <h1 className="text-2xl sm:text-[28px] font-bold text-gray-900 tracking-tight">
                        Your Addresses
                      </h1>
                      <p className="text-xs text-gray-500 mt-1">
                        Edit delivery addresses for orders and gifts.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => setActiveView('hub')}
                      className="px-4 py-2 border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50 cursor-pointer self-start sm:self-auto flex items-center gap-1.5"
                    >
                      <ChevronLeft className="w-3.5 h-3.5" />
                      <span>Back to Your Account</span>
                    </button>
                  </div>

                  {/* Address Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">

                    {/* CARD 1: ADD ADDRESS DASHED CARD (AMAZON STYLE) */}
                    <div
                      onClick={() => setShowAddModal(true)}
                      className="border-2 border-dashed border-gray-300 hover:border-gray-400 rounded-lg p-8 flex flex-col items-center justify-center text-center min-h-[220px] cursor-pointer hover:bg-gray-50 transition-all bg-white"
                    >
                      <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 mb-3">
                        <Plus className="w-7 h-7 text-gray-400" />
                      </div>
                      <h3 className="text-base font-bold text-gray-900">
                        Add address
                      </h3>
                    </div>

                    {/* SAVED ADDRESS CARDS */}
                    {addresses.map((addr) => {
                      const addrId = addr._id || addr.id;
                      const isDef = addr.isDefault !== false;
                      const tagLabel = (addr.addressType || addr.type || 'HOME').toUpperCase();

                      return (
                        <div
                          key={addrId}
                          className="border border-gray-300 rounded-lg p-5 bg-white relative flex flex-col justify-between shadow-2xs min-h-[220px]"
                        >
                          <div>
                            {/* Top row */}
                            <div className="flex items-center justify-between mb-3 text-xs">
                              {isDef && (
                                <span className="font-bold text-gray-500 text-[11px] uppercase tracking-wider">
                                  Default: <strong className="text-gray-900">Delivery</strong>
                                </span>
                              )}
                              <span className="px-2 py-0.5 rounded bg-gray-100 text-[10px] font-semibold text-gray-600 uppercase">
                                {tagLabel}
                              </span>
                            </div>

                            <h4 className="font-bold text-sm text-gray-900 mb-1">
                              {addr.fullName || addr.name || customer.name || 'Hariharan M'}
                            </h4>

                            <p className="text-xs text-gray-700 leading-relaxed capitalize">
                              {addr.addressLine1 || addr.line1 || '385 anna nagar namakkal road'}
                            </p>
                            {addr.addressLine2 && (
                              <p className="text-xs text-gray-600 leading-relaxed capitalize">
                                {addr.addressLine2}
                              </p>
                            )}
                            <p className="text-xs text-gray-700 leading-relaxed mt-0.5">
                              {addr.city || 'Tiruchengode'}, {addr.state || 'Tamil Nadu'} {addr.pincode || '637211'}
                            </p>
                            <p className="text-xs text-gray-700 leading-relaxed">
                              India
                            </p>
                            {(addr.phone || customer.phone) && (
                              <p className="text-xs text-gray-600 mt-2">
                                Phone number: {addr.phone || customer.phone}
                              </p>
                            )}
                          </div>

                          {/* Action Links */}
                          <div className="pt-4 mt-3 border-t border-gray-100 flex items-center gap-3 text-xs">
                            <button
                              type="button"
                              onClick={() => handleDeleteAddress(addrId)}
                              className="text-[#007185] hover:text-[#C7511F] hover:underline cursor-pointer font-medium"
                            >
                              Remove
                            </button>
                            <span>|</span>
                            <button
                              type="button"
                              onClick={() => setShowAddModal(true)}
                              className="text-[#007185] hover:text-[#C7511F] hover:underline cursor-pointer font-medium"
                            >
                              Edit
                            </button>
                          </div>
                        </div>
                      );
                    })}

                  </div>

                </div>
              )}

              {/* ────────────────── VIEW 4: "LOGIN & SECURITY" DEEP DIVE ───── */}
              {activeView === 'security' && (
                <div>
                  {/* Breadcrumb Navigation */}
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-6">
                    <button
                      onClick={() => setActiveView('hub')}
                      className="text-[#007185] hover:text-[#C7511F] hover:underline font-medium cursor-pointer"
                    >
                      Your Account
                    </button>
                    <span>›</span>
                    <span className="text-[#C7511F] font-bold">Login &amp; security</span>
                  </div>

                  {/* Header Row */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 pb-4 border-b border-gray-200">
                    <div>
                      <h1 className="text-2xl sm:text-[28px] font-bold text-gray-900 tracking-tight">
                        Login &amp; security
                      </h1>
                      <p className="text-xs text-gray-500 mt-1">
                        Edit your name, mobile number, and login settings.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => setActiveView('hub')}
                      className="px-4 py-2 border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50 cursor-pointer self-start sm:self-auto flex items-center gap-1.5"
                    >
                      <ChevronLeft className="w-3.5 h-3.5" />
                      <span>Back to Your Account</span>
                    </button>
                  </div>

                  {/* Security Rows (Amazon Style) */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden bg-white max-w-2xl divide-y divide-gray-200">
                    
                    {/* Row 1: Name */}
                    <div className="p-4 sm:p-5 flex items-center justify-between gap-4">
                      <div>
                        <span className="block font-bold text-sm text-gray-900">Name</span>
                        <span className="text-xs text-gray-600">{customer.name || 'Hariharan M'}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setEditingProfile(true)}
                        className="px-4 py-1.5 border border-gray-300 rounded-md text-xs font-medium hover:bg-gray-50 shadow-2xs cursor-pointer"
                      >
                        Edit
                      </button>
                    </div>

                    {/* Row 2: Email */}
                    <div className="p-4 sm:p-5 flex items-center justify-between gap-4">
                      <div>
                        <span className="block font-bold text-sm text-gray-900">Email</span>
                        <span className="text-xs text-gray-600">{customer.email || 'hariharan152578@gmail.com'}</span>
                      </div>
                      <span className="text-xs text-emerald-700 font-semibold bg-emerald-50 px-2.5 py-0.5 rounded border border-emerald-200">
                        Verified via Google
                      </span>
                    </div>

                    {/* Row 3: Mobile Number */}
                    <div className="p-4 sm:p-5 flex items-center justify-between gap-4">
                      <div>
                        <span className="block font-bold text-sm text-gray-900">Mobile Phone Number</span>
                        <span className="text-xs text-gray-600">{customer.phone || '8870295336'}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setEditingProfile(true)}
                        className="px-4 py-1.5 border border-gray-300 rounded-md text-xs font-medium hover:bg-gray-50 shadow-2xs cursor-pointer"
                      >
                        Edit
                      </button>
                    </div>

                    {/* Row 4: Account Security */}
                    <div className="p-4 sm:p-5 flex items-center justify-between gap-4">
                      <div>
                        <span className="block font-bold text-sm text-gray-900">Two-Step Verification (2SV)</span>
                        <span className="text-xs text-gray-600">Active and managed via your Google Identity account</span>
                      </div>
                      <span className="text-xs text-gray-500 font-medium">
                        Active
                      </span>
                    </div>

                  </div>

                  {/* Edit Profile Form Inline */}
                  {editingProfile && (
                    <div className="mt-6 border border-gray-200 rounded-lg p-6 bg-gray-50/70 max-w-2xl">
                      <h3 className="font-bold text-sm text-gray-900 mb-3">Edit Name &amp; Phone Number</h3>
                      <form onSubmit={handleUpdateProfile} className="space-y-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Full Name</label>
                          <input
                            type="text"
                            required
                            value={profileForm.name}
                            onChange={(e) => setProfileForm((p) => ({ ...p, name: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-xs bg-white focus:outline-none focus:border-[#FF9900]"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Mobile Phone Number</label>
                          <input
                            type="text"
                            value={profileForm.phone}
                            onChange={(e) => setProfileForm((p) => ({ ...p, phone: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-xs bg-white focus:outline-none focus:border-[#FF9900]"
                          />
                        </div>

                        <div className="flex gap-2 pt-2">
                          <button
                            type="submit"
                            className="px-5 py-2 bg-[#FFD814] hover:bg-[#F7CA00] text-gray-900 text-xs font-semibold rounded-md border border-[#FCD200] cursor-pointer shadow-2xs"
                          >
                            Save Changes
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingProfile(false)}
                            className="px-4 py-2 border border-gray-300 rounded-md text-xs text-gray-700 hover:bg-gray-100 cursor-pointer"
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  )}

                </div>
              )}

              {/* ────────────────── VIEW 5: "CONTACT US" DEEP DIVE ─────────── */}
              {activeView === 'contact' && (
                <div>
                  {/* Breadcrumb Navigation */}
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-6">
                    <button
                      onClick={() => setActiveView('hub')}
                      className="text-[#007185] hover:text-[#C7511F] hover:underline font-medium cursor-pointer"
                    >
                      Your Account
                    </button>
                    <span>›</span>
                    <span className="text-[#C7511F] font-bold">Contact Us</span>
                  </div>

                  {/* Header Row */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 pb-4 border-b border-gray-200">
                    <div>
                      <h1 className="text-2xl sm:text-[28px] font-bold text-gray-900 tracking-tight">
                        Customer Service
                      </h1>
                      <p className="text-xs text-gray-500 mt-1">
                        We&apos;re here to help with your orders, dispatch, and custom box enquiries.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => setActiveView('hub')}
                      className="px-4 py-2 border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50 cursor-pointer self-start sm:self-auto flex items-center gap-1.5"
                    >
                      <ChevronLeft className="w-3.5 h-3.5" />
                      <span>Back to Your Account</span>
                    </button>
                  </div>

                  {/* Contact Options Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-3xl">
                    
                    {/* Option 1: WhatsApp Support */}
                    <div className="border border-gray-200 rounded-lg p-6 bg-white space-y-3 shadow-2xs">
                      <div className="w-12 h-12 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center">
                        <MessageCircle className="w-6 h-6" />
                      </div>
                      <h3 className="font-bold text-base text-gray-900">
                        WhatsApp Concierge
                      </h3>
                      <p className="text-xs text-gray-600 leading-relaxed">
                        Instant support for order tracking, special wedding packaging, and live kitchen dispatch queries.
                      </p>
                      <a
                        href="https://wa.me/919443210890?text=Vanakkam%20Elavarasi%20Foods%20Support%2C%20I%20need%20help%20with%20my%20account%20or%20order."
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white font-medium text-xs rounded-md shadow-2xs transition-colors"
                      >
                        <MessageCircle className="w-4 h-4" />
                        <span>Chat on WhatsApp</span>
                      </a>
                    </div>

                    {/* Option 2: Phone & Central Kitchen Support */}
                    <div className="border border-gray-200 rounded-lg p-6 bg-white space-y-3 shadow-2xs">
                      <div className="w-12 h-12 rounded-full bg-teal-50 text-teal-700 flex items-center justify-center">
                        <Phone className="w-6 h-6" />
                      </div>
                      <h3 className="font-bold text-base text-gray-900">
                        Direct Phone Support
                      </h3>
                      <p className="text-xs text-gray-600 leading-relaxed">
                        Speak directly with our central kitchen support desk in Tiruchengode, Tamil Nadu.
                      </p>
                      <div className="text-xs font-mono font-bold text-gray-900">
                        +91 94432 10890 (8:30 AM – 9:00 PM IST)
                      </div>
                      <a
                        href="tel:+919443210890"
                        className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-[#FFD814] hover:bg-[#F7CA00] text-gray-900 font-semibold text-xs rounded-md border border-[#FCD200] shadow-2xs transition-colors"
                      >
                        <Phone className="w-4 h-4" />
                        <span>Call Support Desk</span>
                      </a>
                    </div>

                  </div>

                </div>
              )}

              {/* ────────────────── VIEW 6: "PRIME / PATRON" DEEP DIVE ─────── */}
              {activeView === 'prime' && (
                <div>
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-6">
                    <button
                      onClick={() => setActiveView('hub')}
                      className="text-[#007185] hover:text-[#C7511F] hover:underline font-medium cursor-pointer"
                    >
                      Your Account
                    </button>
                    <span>›</span>
                    <span className="text-[#C7511F] font-bold">Elavarasi Prime</span>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 pb-4 border-b border-gray-200">
                    <div>
                      <h1 className="text-2xl sm:text-[28px] font-bold text-gray-900 tracking-tight">
                        Elavarasi Prime Membership
                      </h1>
                      <p className="text-xs text-gray-500 mt-1">
                        Enjoy free express dispatch, priority kitchen packing, and festive pre-booking privileges.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => setActiveView('hub')}
                      className="px-4 py-2 border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50 cursor-pointer self-start sm:self-auto flex items-center gap-1.5"
                    >
                      <ChevronLeft className="w-3.5 h-3.5" />
                      <span>Back to Your Account</span>
                    </button>
                  </div>

                  <div className="border border-gray-200 rounded-lg p-6 sm:p-8 bg-white max-w-2xl space-y-5 shadow-2xs">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#00A8E1] to-[#007EA7] text-white flex items-center justify-center">
                        <Crown className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="font-bold text-base text-gray-900">Prime Patron Status: Active</h3>
                        <p className="text-xs text-gray-500">Member since 2026 • Free express delivery over ₹499</p>
                      </div>
                    </div>

                    <div className="border-t border-gray-100 pt-4 space-y-3 text-xs text-gray-700">
                      <div className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                        <span><strong>Free Express Air Dispatch</strong> across India on qualifying orders</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                        <span><strong>Priority Cauldrons:</strong> Fresh batch fried and packed within 12 hours</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                        <span><strong>Festive Pre-Booking:</strong> Guaranteed delivery for Deepavali, Pongal &amp; Gokulashtami</span>
                      </div>
                    </div>

                    <div className="pt-2">
                      <Link
                        href="/shop"
                        className="px-6 py-2.5 bg-[#FFD814] hover:bg-[#F7CA00] text-gray-900 font-semibold text-xs rounded-full border border-[#FCD200] inline-block shadow-2xs"
                      >
                        Shop with Prime Benefits
                      </Link>
                    </div>
                  </div>
                </div>
              )}



              {/* ────────────────── VIEW 8: "PAYMENT OPTIONS" DEEP DIVE ────── */}
              {activeView === 'payment' && (
                <div>
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-6">
                    <button
                      onClick={() => setActiveView('hub')}
                      className="text-[#007185] hover:text-[#C7511F] hover:underline font-medium cursor-pointer"
                    >
                      Your Account
                    </button>
                    <span>›</span>
                    <span className="text-[#C7511F] font-bold">Payment options</span>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 pb-4 border-b border-gray-200">
                    <div>
                      <h1 className="text-2xl sm:text-[28px] font-bold text-gray-900 tracking-tight">
                        Payment Options
                      </h1>
                      <p className="text-xs text-gray-500 mt-1">
                        Manage saved payment methods and checkout preferences.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => setActiveView('hub')}
                      className="px-4 py-2 border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50 cursor-pointer self-start sm:self-auto flex items-center gap-1.5"
                    >
                      <ChevronLeft className="w-3.5 h-3.5" />
                      <span>Back to Your Account</span>
                    </button>
                  </div>

                  <div className="max-w-2xl border border-gray-200 rounded-lg p-6 sm:p-7 bg-white space-y-4 shadow-2xs">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-lg bg-teal-50 text-teal-700 flex items-center justify-center">
                        <CreditCard className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="font-bold text-base text-gray-900">Supported Payment Methods</h3>
                        <p className="text-xs text-gray-500">Fast, secure 1-click checkout for all orders</p>
                      </div>
                    </div>

                    <p className="text-xs text-gray-600 leading-relaxed pt-1">
                      We support instant UPI (Google Pay, PhonePe, Paytm, BHIM), Net Banking across 50+ major Indian banks, all Visa / Mastercard / RuPay credit and debit cards, and Cash on Delivery (COD).
                    </p>

                    <div className="pt-2 flex items-center gap-2">
                      <span className="inline-block text-xs text-emerald-700 font-semibold bg-emerald-50 px-3 py-1 rounded-md border border-emerald-200">
                        Secured via Razorpay 256-Bit SSL Encryption
                      </span>
                    </div>
                  </div>
                </div>
              )}

            </div>
          )}

        </div>
      </main>

      {/* ── MODAL: ADD NEW ADDRESS ────────────────────────────────────────── */}
      <AnimatePresence>
        {showAddModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-2xs">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-lg shadow-2xl border border-gray-300 max-w-lg w-full overflow-hidden"
            >
              {/* Modal Header */}
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-base text-gray-900">Add a new address</h3>
                  <p className="text-xs text-gray-500">Or pick up from an Elavarasi store</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="text-gray-400 hover:text-gray-700 p-1 rounded-md cursor-pointer"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Modal Form */}
              <form onSubmit={handleCreateAddress} className="p-6 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      Full name (First and Last name) *
                    </label>
                    <input
                      type="text"
                      required
                      value={newAddress.fullName}
                      onChange={(e) => setNewAddress((p) => ({ ...p, fullName: e.target.value }))}
                      placeholder="e.g. Hariharan M"
                      className="w-full px-3 py-2 rounded-md border border-gray-300 text-xs text-gray-900 focus:outline-none focus:border-[#FF9900]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      Mobile number *
                    </label>
                    <input
                      type="text"
                      required
                      value={newAddress.phone}
                      onChange={(e) => setNewAddress((p) => ({ ...p, phone: e.target.value }))}
                      placeholder="10-digit mobile number"
                      className="w-full px-3 py-2 rounded-md border border-gray-300 text-xs text-gray-900 focus:outline-none focus:border-[#FF9900] font-mono"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    Flat, House no., Building, Company, Apartment *
                  </label>
                  <input
                    type="text"
                    required
                    value={newAddress.addressLine1}
                    onChange={(e) => setNewAddress((p) => ({ ...p, addressLine1: e.target.value }))}
                    placeholder="e.g. 385 Anna Nagar, Namakkal Main Road"
                    className="w-full px-3 py-2 rounded-md border border-gray-300 text-xs text-gray-900 focus:outline-none focus:border-[#FF9900]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    Area, Street, Sector, Village (Optional)
                  </label>
                  <input
                    type="text"
                    value={newAddress.addressLine2}
                    onChange={(e) => setNewAddress((p) => ({ ...p, addressLine2: e.target.value }))}
                    placeholder="Nearby Landmark"
                    className="w-full px-3 py-2 rounded-md border border-gray-300 text-xs text-gray-900 focus:outline-none focus:border-[#FF9900]"
                  />
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      Town/City *
                    </label>
                    <input
                      type="text"
                      required
                      value={newAddress.city}
                      onChange={(e) => setNewAddress((p) => ({ ...p, city: e.target.value }))}
                      placeholder="Tiruchengode"
                      className="w-full px-3 py-2 rounded-md border border-gray-300 text-xs text-gray-900 focus:outline-none focus:border-[#FF9900]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      State *
                    </label>
                    <select
                      value={newAddress.state}
                      onChange={(e) => setNewAddress((p) => ({ ...p, state: e.target.value }))}
                      className="w-full px-2.5 py-2 rounded-md border border-gray-300 text-xs text-gray-900 focus:outline-none focus:border-[#FF9900] bg-white cursor-pointer"
                    >
                      {INDIAN_STATES.map((st) => (
                        <option key={st} value={st}>
                          {st}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      Pincode *
                    </label>
                    <input
                      type="text"
                      required
                      maxLength={6}
                      value={newAddress.pincode}
                      onChange={(e) => setNewAddress((p) => ({ ...p, pincode: e.target.value.replace(/[^0-9]/g, '') }))}
                      placeholder="637211"
                      className="w-full px-3 py-2 rounded-md border border-gray-300 text-xs text-gray-900 focus:outline-none focus:border-[#FF9900] font-mono"
                    />
                  </div>
                </div>

                {/* Address Type Selector */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">
                    Address Type
                  </label>
                  <div className="flex gap-2">
                    {(['HOME', 'OFFICE', 'OTHER'] as const).map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setNewAddress((p) => ({ ...p, addressType: type }))}
                        className={`flex-1 py-2 px-3 rounded-md text-xs font-semibold border transition-all cursor-pointer ${
                          newAddress.addressType === type
                            ? 'bg-amber-50 border-[#FF9900] text-gray-900'
                            : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Default checkbox */}
                <label className="flex items-center gap-2 pt-1 text-xs text-gray-700 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={newAddress.isDefault}
                    onChange={(e) => setNewAddress((p) => ({ ...p, isDefault: e.target.checked }))}
                    className="rounded text-[#FF9900] focus:ring-[#FF9900]"
                  />
                  <span>Make this my default address</span>
                </label>

                {/* Submit & Cancel */}
                <div className="flex justify-end gap-3 pt-3 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-4 py-2 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-100 cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submittingAddress}
                    className="px-6 py-2 bg-[#FFD814] hover:bg-[#F7CA00] text-gray-900 font-semibold text-xs rounded-md border border-[#FCD200] shadow-2xs transition-all cursor-pointer disabled:opacity-50"
                  >
                    {submittingAddress ? 'Saving...' : 'Add address'}
                  </button>
                </div>

              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Dynamic Language Preference Notification Toast */}
      {showLanguageToast && (
        <div className="fixed bottom-6 right-6 z-50 bg-neutral-900 text-white px-4 py-2.5 rounded-lg shadow-xl text-xs font-medium flex items-center space-x-2 border border-neutral-700 animate-in fade-in slide-in-from-bottom-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
          <span>Language switched to {selectedLanguage === 'en' ? 'English' : 'தமிழ் (Tamil)'}</span>
        </div>
      )}

      <Footer />
    </>
  );
}
